import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Route, Routes, Navigate, BrowserRouter, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Container from "./components/Container/Container";
import DashboardScreen from "./pages/DashboardScreen";
import ClientListScreen from "./pages/clients/ClientListScreen";
import ProductListScreen from "./pages/products/ProductListScreen";
import InvoiceListScreen from "./pages/invoices/InvoiceListScreen";
import InvoiceDetailScreen from "./pages/invoices/InvoiceDetailScreen";
import QuotationListScreen from "./pages/quotations/QuotationListScreen";
import QuotationDetailScreen from "./pages/quotations/QuotationDetailScreen";
import GSTScreen from "./pages/gst/GSTScreen";
import LandingPage from "./pages/LandingPage";
import LoginScreen from "./pages/LoginScreen";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import ProfileScreen from "./pages/profile/ProfileScreen";
import SubscriptionScreen from "./pages/subscription/SubscriptionScreen";
// Admin App (separate)
import AdminApp from "./pages/admin/AdminApp";
import useInitApp from "./hook/useInitApp";
import ClientDeleteConfirm from "./components/Clients/ClientDeleteConfirm";
import ClientEditModal from "./components/Clients/ClientEditModal";
import ProductDeleteConfirm from "./components/Product/ProductDeleteConfirm";
import ProductEditModal from "./components/Product/ProductEditModal";
import InvoiceSettingModal from "./components/Invoice/InvoiceSettingModal";
import InvoiceConfirmModal from "./components/Invoice/InvoiceConfirmModal";
import InvoiceDeleteConfirm from "./components/Invoice/InvoiceDeleteConfirm";
import QuotationSettingModal from "./components/Quotation/QuotationSettingModal";
import QuotationConfirmModal from "./components/Quotation/QuotationConfirmModal";
import QuotationDeleteConfirm from "./components/Quotation/QuotationDeleteConfirm";
import QuotationStateReset from "./components/Quotation/QuotationStateReset";
import UsageLimitModal from "./components/UsageRestriction/UsageLimitModal";
import userService from "./services/userService";
import { useCloudSync } from "./hooks/useCloudSync";
import PhoneVerificationModal from "./components/Auth/PhoneVerificationModal";
import { supabase } from "./config/supabase";
import {
  clearGoogleAuth,
  getStoredGoogleAuth,
  persistGoogleAuth,
} from "./services/googleTokenService";
import useSubscriptionMonitor from "./hooks/useSubscriptionMonitor";
// Remove useUsageTracking import to fix render loop
// import useUsageTracking from "./hooks/useUsageTracking";

const AppContent = () => {
  const { initialSetData } = useInitApp();
  const { initializeSync } = useCloudSync();
  const location = useLocation();
  // Do NOT seed from userService/localStorage. currentUser is set only after
  // syncFromAuthSession completes, so no Supabase-backed UI renders before the
  // JWT is ready (avoids 401/406 on first queries after OAuth redirect).
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [showUsageLimitModal, setShowUsageLimitModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  
  // Handle redirect after login
  const urlParams = new URLSearchParams(location.search);
  const redirectPath = urlParams.get('redirect');

  // Monitor subscription expiry
  useSubscriptionMonitor();

  useEffect(() => {
    initialSetData();
  }, [initialSetData]);

  const bootstrapStoredToken = useCallback(async () => {
    const storedAuth = getStoredGoogleAuth();
    if (storedAuth?.accessToken) {
      try {
        await initializeSync(storedAuth.accessToken, storedAuth.userId);
      } catch (error) {
        // Silent - cloud sync initialization errors should not expose details
      }
    }
  }, [initializeSync]);

  useEffect(() => {
    bootstrapStoredToken();
  }, [bootstrapStoredToken]);

  const syncSessionUser = useCallback(async (session) => {
    if (!session) {
      userService.clearLocalSession();
      clearGoogleAuth();
      setCurrentUser(null);
      setSessionReady(true);
      setIsLoading(false);
      return;
    }

    // Reject sessions that are already expired by the client's clock. On some
    // devices the OAuth redirect lands with an access_token whose exp is in
    // the past — either because the system clock is skewed (hours off) or the
    // user opened a stale OAuth-redirect URL. If we let syncFromAuthSession
    // run anyway, the dead JWT hits RLS (406 on users, 401 on usage_logs) and
    // supabase-js loops on refresh_token (429).
    const nowSec = Math.floor(Date.now() / 1000);
    if (session.expires_at && session.expires_at <= nowSec) {
      console.warn(
        `Rejecting expired Supabase session (expired ${nowSec - session.expires_at}s ago). ` +
        `Likely causes: device clock is wrong, or a stale OAuth redirect URL was reopened.`
      );
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (e) { /* ignore — local clear is best-effort */ }
      // Strip the OAuth fragment so a reload doesn't replay the same dead tokens.
      if (window.location.hash && window.location.hash.includes('access_token')) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      userService.clearLocalSession();
      clearGoogleAuth();
      setCurrentUser(null);
      setSessionReady(true);
      setIsLoading(false);
      return;
    }

    try {
      await userService.syncFromAuthSession(session);
      const syncedUser = userService.getCurrentUser();
      setCurrentUser(syncedUser);
      setSessionReady(true);
      setIsLoading(false);

      if (session.provider_token) {
        const identifier = syncedUser?.email || session.user?.email || session.user?.id;
        if (identifier) {
          persistGoogleAuth(session.provider_token, identifier);
          try {
            await initializeSync(session.provider_token, identifier);
          } catch (error) {
            // Silent - cloud sync initialization errors should not expose details
          }
        }
      }
    } catch (error) {
      console.error('Failed to sync Supabase session', error);
      // Sync failed (bad JWT, RLS, network). Don't leave the UI in a
      // half-authenticated state — force signed-out so nothing queries Supabase.
      userService.clearLocalSession();
      clearGoogleAuth();
      setCurrentUser(null);
      setSessionReady(true);
      setIsLoading(false);
    }
  }, [initializeSync]);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION on subscribe (supabase-js >= 2.7),
    // so it replaces a separate getSession() call. Running both caused two
    // concurrent syncFromAuthSession flows on OAuth redirect, which hammered
    // /users + /usage_logs and tripped the refresh-token rate limit.
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      syncSessionUser(session);
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [syncSessionUser]);

  useEffect(() => {
    if (!currentUser) {
      setShowPhoneModal(false);
      return;
    }

    if (!currentUser.phone_number) {
      setShowPhoneModal(true);
    } else {
      setShowPhoneModal(false);
    }
  }, [currentUser]);

  // If not signed in, show login page only at /signin

  // Avoid rendering the private app shell on legal/public pages so consent screen links
  // always show the landing-style pages even for signed-in users
  const legalPaths = useMemo(() => ["/privacy-policy", "/terms-of-service"], []);
  const isLegalPath = useMemo(() => legalPaths.includes(location.pathname), [legalPaths, location.pathname]);
  // Gate authenticated UI on sessionReady: no Supabase-backed route renders
  // until syncFromAuthSession has settled, even if currentUser is set from a
  // stale source in the future.
  const shouldShowPublicShell = !currentUser || !sessionReady || isLegalPath;

  // Show loading screen during session hydration
  if (isLoading || !sessionReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Admin Application - Completely separate from main app */}
        <Route path="/admin/*" element={<AdminApp />} />
        
        {/* Main Application Routes */}
        {shouldShowPublicShell ? (
          <>
            <Route path="/" element={<LandingPage />} />
            <Route path="/signin" element={<LoginScreen redirectPath={redirectPath} />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <Route path="/*" element={
            <Container>
              {/* Render client and product modals globally so edit/delete always work */}
              <ClientEditModal />
              <ClientDeleteConfirm />
              <ProductEditModal />
              <ProductDeleteConfirm />
              {/* Render invoice modals globally so setting, save, and delete actions work, aligned with sidebar */}
              <InvoiceSettingModal />
              <InvoiceConfirmModal />
              <InvoiceDeleteConfirm />
              
              {/* Render quotation modals globally */}
              <QuotationStateReset />
              <QuotationSettingModal />
              <QuotationConfirmModal />
              <QuotationDeleteConfirm />
              
              {/* User Management Modals */}
              <UsageLimitModal 
                isOpen={showUsageLimitModal}
                onClose={() => setShowUsageLimitModal(false)}
                onUpgrade={() => {
                  setShowUsageLimitModal(false);
                  // Use window.location for App level navigation since it's outside Router context
                  window.location.href = '/subscription';
                }}
              />

              <PhoneVerificationModal
                isOpen={showPhoneModal}
                onClose={() => setShowPhoneModal(false)}
                initialPhoneNumber={currentUser?.phone_number}
                onVerified={() => {
                  setShowPhoneModal(false)
                  // refresh user data to reflect verification
                  const refreshed = userService.getCurrentUser()
                  setCurrentUser(refreshed)
                }}
              />
              
              <Routes>
                <Route path="/" element={<Navigate to="/dashboards" replace />} />
                <Route path="/dashboards" element={<DashboardScreen />} />
                <Route path="/clients" element={<ClientListScreen />} />
                <Route path="/products" element={<ProductListScreen />} />
                <Route path="/invoices">
                  <Route path="" element={<InvoiceListScreen />} />
                  <Route path=":id" element={<InvoiceDetailScreen />} />
                </Route>
                <Route path="/quotations">
                  <Route path="" element={<QuotationListScreen />} />
                  <Route path=":id" element={<QuotationDetailScreen />} />
                </Route>
                <Route path="/gst" element={<GSTScreen />} />
                
                <Route path="/profile" element={<ProfileScreen />} />
                <Route path="/subscription" element={<SubscriptionScreen />} />
                
                <Route path="*" element={<Navigate to="/dashboards" replace />} />
              </Routes>
            </Container>
          } />
        )}
      </Routes>
      <ToastContainer />
    </>
  );
};

const App = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
